import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled =
      this.dataset.addToCartAnimation === 'true';

    if (
      animationEnabled &&
      !event.target.closest('.quick-add-modal')
    ) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;

    const cartIcon =
      document.querySelector(
        '.header-actions__cart-icon'
      );

    const image =
      this.dataset.productVariantMedia;

    if (
      !cartIcon ||
      !addToCartButton ||
      !image
    ) {
      return;
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement(
          'fly-to-cart'
        )
      );

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.source =
      addToCartButton;

    flyToCartElement.destination =
      cartIcon;

    document.body.appendChild(
      flyToCartElement
    );
  }

  animateAddToCart() {
    const { addToCartButton } =
      this.refs;

    if (this.#animationTimeout) {
      clearTimeout(
        this.#animationTimeout
      );
    }

    if (this.#cleanupTimeout) {
      clearTimeout(
        this.#cleanupTimeout
      );
    }

    if (
      !addToCartButton.classList.contains(
        'atc-added'
      )
    ) {
      addToCartButton.classList.add(
        'atc-added'
      );
    }

    this.#animationTimeout =
      setTimeout(() => {
        this.#cleanupTimeout =
          setTimeout(() => {
            this.refs.addToCartButton
              .classList.remove(
                'atc-added'
              );
          }, 10);
      }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (
  !customElements.get(
    'add-to-cart-component'
  )
) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}


/**
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = [
    'variantId',
    'liveRegion',
  ];

  #abortController =
    new AbortController();

  /** @type {number | undefined} */
  #timeout;


  connectedCallback() {
    super.connectedCallback();

    const { signal } =
      this.#abortController;

    const target =
      this.closest(
        '.shopify-section, dialog, product-card'
      );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );


    /*
     * PIZSY 1 / 2 / 3 BUNDLE
     */

    const productDetails =
      this.closest(
        '[data-testid="product-information-details"]'
      );

    const bundleRoot =
      productDetails?.querySelector(
        '[data-pz-bundle-root]'
      );


    if (
      bundleRoot instanceof HTMLElement
    ) {
      /*
       * A darabszámot már nem a régi
       * Shopify quantity selector kezeli.
       */

      const quantitySelector =
        this.querySelector(
          '.quantity-selector'
        );

      quantitySelector?.setAttribute(
        'hidden',
        'true'
      );


      const quantityInput =
        this.querySelector(
          'input[name="quantity"]'
        );

      if (
        quantityInput instanceof
        HTMLInputElement
      ) {
        quantityInput.value = '1';
      }


      /*
       * Gyors checkoutot nem használunk,
       * mert megkerülné a bundle logikát.
       */

      this.refs
        .acceleratedCheckoutButtonContainer
        ?.setAttribute(
          'hidden',
          'true'
        );
    }
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }


  /**
   * @param {Event} event
   */
  handleSubmit(event) {
    const {
      addToCartTextError,
    } = this.refs;


    event.preventDefault();


    if (this.#timeout) {
      clearTimeout(
        this.#timeout
      );
    }


    if (
      this.refs
        .addToCartButtonContainer
        ?.refs.addToCartButton
        ?.getAttribute(
          'disabled'
        ) === 'true'
    ) {
      return;
    }


    const form =
      this.querySelector('form');


    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }


    const formData =
      new FormData(form);


    const productVariantId =
      Number(
        formData.get('id')
      );


    const quantity =
      Number(
        formData.get(
          'quantity'
        )
      ) ||
      Number(
        this.dataset.quantityDefault
      ) ||
      1;


    /*
     * HELYI HIBAÜZENET
     */

    const showLocalError =
      (message) => {

        this.#setLiveRegionText(
          message
        );


        if (addToCartTextError) {
          addToCartTextError
            .classList.remove(
              'hidden'
            );


          const textNode =
            addToCartTextError
              .childNodes[2];


          if (textNode) {
            textNode.textContent =
              message;
          } else {
            addToCartTextError
              .appendChild(
                document
                  .createTextNode(
                    message
                  )
              );
          }
        }


        this.#timeout =
          setTimeout(() => {

            if (
              addToCartTextError
            ) {
              addToCartTextError
                .classList.add(
                  'hidden'
                );
            }


            this
              .#clearLiveRegionText();

          }, 10000);
      };


    /*
     * CART SECTIONÖK
     */

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );


    const cartItemComponentsSectionIds =
      [];


    cartItemsComponents
      .forEach((item) => {

        if (
          item instanceof
            HTMLElement &&
          item.dataset.sectionId &&
          !cartItemComponentsSectionIds
            .includes(
              item.dataset.sectionId
            )
        ) {
          cartItemComponentsSectionIds
            .push(
              item.dataset.sectionId
            );
        }

      });


    /*
     * MEGNÉZZÜK, HOGY EZ
     * A PIZSY BUNDLE-E.
     */

    const productDetails =
      this.closest(
        '[data-testid="product-information-details"]'
      );


    const bundleRoot =
      productDetails
        ?.querySelector(
          '[data-pz-bundle-root]'
        );


    const isPizsyBundle =
      bundleRoot instanceof
      HTMLElement;


    let fetchCfg;

    let addedItemCount =
      quantity;


    /*
     * ==========================================
     * ÚJ PIZSY BUNDLE
     * ==========================================
     */

    if (isPizsyBundle) {

      if (!productVariantId) {
        showLocalError(
          'Az első Pizsy variációja nem található. Kérlek válassz életkort és mintát.'
        );

        return;
      }


      const selectedBundleInput =
        bundleRoot.querySelector(
          '[data-pz-bundle-qty]:checked'
        );


      let bundleQuantity =
        Number(
          bundleRoot
            .dataset
            .pzBundleQuantity ||
          selectedBundleInput
            ?.value ||
          1
        );


      if (
        ![1, 2, 3]
          .includes(
            bundleQuantity
          )
      ) {
        bundleQuantity = 1;
      }


      /*
       * Megtartjuk az egyéb
       * product property-ket.
       */

      const commonProductProperties =
        {};


      for (
        const [
          key,
          value,
        ] of formData.entries()
      ) {

        const match =
          key.match(
            /^properties\[(.+)\]$/
          );


        if (
          match &&
          value !== '' &&
          match[1] !==
            'Ajándék filctoll'
        ) {
          commonProductProperties[
            match[1]
          ] = value;
        }
      }


      const items = [];


      /*
       * MINDEN KIVÁLASZTOTT
       * PIZSY VÉGIGJÁRÁSA.
       */

      for (
        let slotNumber = 1;
        slotNumber <=
          bundleQuantity;
        slotNumber += 1
      ) {

        let pizsyVariantId =
          productVariantId;


        /*
         * PIZSY #2 / #3
         */

        if (
          slotNumber > 1
        ) {

          const extraProduct =
            bundleRoot
              .querySelector(
                `[data-pz-extra-product="${slotNumber}"]`
              );


          pizsyVariantId =
            Number(
              extraProduct
                ?.dataset
                .pzVariantId
            );


          const variantAvailable =
            extraProduct
              ?.dataset
              .pzVariantAvailable ===
            'true';


          if (
            !pizsyVariantId ||
            !variantAvailable
          ) {
            showLocalError(
              `A Pizsy #${slotNumber} kiválasztott életkor/minta kombinációja nem elérhető.`
            );

            return;
          }
        }


        /*
         * AZ ADOTT PIZSY
         * SAJÁT FILCTOLLA.
         */

        const markerInput =
          bundleRoot
            .querySelector(
              `[data-pz-marker-choice][data-pz-marker-slot="${slotNumber}"]:checked`
            );


        if (
          !(
            markerInput instanceof
            HTMLInputElement
          )
        ) {

          showLocalError(
            bundleQuantity === 1
              ?
                'Kérlek válaszd ki az ajándék filctollat.'
              :
                `Kérlek válassz ajándék filctollat a Pizsy #${slotNumber} részhez.`
          );

          return;
        }


        const markerVariantId =
          Number(
            markerInput
              .dataset
              .pzMarkerVariant ||
            markerInput
              .dataset
              .giftVariant
          );


        if (
          !markerVariantId
        ) {
          showLocalError(
            `A Pizsy #${slotNumber} ajándék filctolla nem található. Kérlek frissítsd az oldalt.`
          );

          return;
        }


        const markerName =
          markerInput.value ||
          'Filctoll';


        /*
         * PIZSY HOZZÁADÁSA
         */

        items.push({
          id:
            pizsyVariantId,

          quantity:
            1,

          properties: {
            ...commonProductProperties,

            'Ajándék filctoll':
              markerName,

            '_Pizsy csomag':
              `${bundleQuantity} Pizsy`,
          },
        });


        /*
         * FILCTOLL HOZZÁADÁSA
         */

        items.push({
          id:
            markerVariantId,

          quantity:
            1,

          properties: {
            '_Pizsy ajándék':
              'Filctoll',
          },
        });
      }


      /*
       * EGYETLEN CART/ADD KÉRÉS
       */

      const payload = {
        items,
      };


      if (
        cartItemComponentsSectionIds
          .length
      ) {

        payload.sections =
          cartItemComponentsSectionIds
            .join(',');


        payload.sections_url =
          window.location.pathname +
          window.location.search;
      }


      fetchCfg = {
        method:
          'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json',
        },

        body:
          JSON.stringify(
            payload
          ),
      };


      addedItemCount =
        items.reduce(
          (
            total,
            item
          ) =>
            total +
            Number(
              item.quantity ||
              0
            ),
          0
        );

    } else {

      /*
       * ==========================================
       * RÉGI FILCTOLL / EREDETI SHOPIFY
       * ==========================================
       *
       * Ezt azért tartjuk meg,
       * hogy Quick Add és más termékform
       * ne törjön el.
       */

      const selectedGiftMarker =
        Array.from(
          document
            .querySelectorAll(
              'input[name="properties[Ajándék filctoll]"][data-gift-variant]:checked'
            )
        )
        .find(
          (input) => {

            return (
              input instanceof
                HTMLInputElement &&
              input.form ===
                form
            );

          }
        );


      const giftVariantId =
        selectedGiftMarker
          ?
            Number(
              selectedGiftMarker
                .dataset
                .giftVariant
            )
          :
            null;


      if (
        selectedGiftMarker
      ) {

        if (
          !productVariantId ||
          !giftVariantId
        ) {

          showLocalError(
            'Az ajándék filctoll nem található. Kérlek frissítsd az oldalt és próbáld újra.'
          );

          return;
        }


        const productProperties =
          {};


        for (
          const [
            key,
            value,
          ] of formData.entries()
        ) {

          const match =
            key.match(
              /^properties\[(.+)\]$/
            );


          if (
            match &&
            value !== ''
          ) {
            productProperties[
              match[1]
            ] = value;
          }
        }


        const payload = {
          items: [
            {
              id:
                productVariantId,

              quantity:
                quantity,

              properties:
                productProperties,
            },

            {
              id:
                giftVariantId,

              quantity:
                quantity,

              properties: {
                '_Pizsy ajándék':
                  'Filctoll',
              },
            },
          ],
        };


        if (
          cartItemComponentsSectionIds
            .length
        ) {

          payload.sections =
            cartItemComponentsSectionIds
              .join(',');


          payload.sections_url =
            window.location.pathname +
            window.location.search;
        }


        fetchCfg = {
          method:
            'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',
          },

          body:
            JSON.stringify(
              payload
            ),
        };


        addedItemCount =
          quantity * 2;

      } else {

        /*
         * EREDETI SHOPIFY
         */

        if (
          cartItemComponentsSectionIds
            .length
        ) {

          formData.append(
            'sections',
            cartItemComponentsSectionIds
              .join(',')
          );
        }


        fetchCfg =
          fetchConfig(
            'javascript',
            {
              body:
                formData,
            }
          );


        fetchCfg.headers = {
          ...fetchCfg.headers,

          Accept:
            'text/html',
        };


        addedItemCount =
          quantity;
      }
    }


    /*
     * ==========================================
     * CART REQUEST
     * ==========================================
     */

    fetch(
      Theme.routes.cart_add_url,
      fetchCfg
    )
      .then(
        (response) =>
          response.json()
      )

      .then(
        (response) => {

          /*
           * HIBA
           */

          if (
            response.status
          ) {

            this.dispatchEvent(
              new CartErrorEvent(
                form.getAttribute(
                  'id'
                ) || '',
                response.message,
                response.description,
                response.errors
              )
            );


            if (
              !addToCartTextError
            ) {
              return;
            }


            addToCartTextError
              .classList.remove(
                'hidden'
              );


            const textNode =
              addToCartTextError
                .childNodes[2];


            if (textNode) {

              textNode
                .textContent =
                  response.message;

            } else {

              const newTextNode =
                document
                  .createTextNode(
                    response.message
                  );


              addToCartTextError
                .appendChild(
                  newTextNode
                );
            }


            this
              .#setLiveRegionText(
                response.message
              );


            this.#timeout =
              setTimeout(
                () => {

                  if (
                    !addToCartTextError
                  ) {
                    return;
                  }


                  addToCartTextError
                    .classList.add(
                      'hidden'
                    );


                  this
                    .#clearLiveRegionText();

                },
                10000
              );


            this.dispatchEvent(
              new CartAddEvent(
                {},
                this.id,
                {
                  didError:
                    true,

                  source:
                    'product-form-component',

                  itemCount:
                    addedItemCount,

                  productId:
                    this.dataset.productId,
                }
              )
            );


            return;
          }


          /*
           * SIKER
           */

          const id =
            formData.get(
              'id'
            );


          if (
            addToCartTextError
          ) {

            addToCartTextError
              .classList.add(
                'hidden'
              );


            addToCartTextError
              .removeAttribute(
                'aria-live'
              );
          }


          if (!id) {

            throw new Error(
              'Form ID is required'
            );
          }


          if (
            this.refs
              .addToCartButtonContainer
              ?.refs
              .addToCartButton
          ) {

            const addToCartButton =
              this.refs
                .addToCartButtonContainer
                .refs
                .addToCartButton;


            const addedTextElement =
              addToCartButton
                .querySelector(
                  '.add-to-cart-text--added'
                );


            const addedText =
              addedTextElement
                ?.textContent
                ?.trim() ||
              Theme
                .translations
                .added;


            this
              .#setLiveRegionText(
                addedText
              );


            setTimeout(
              () => {

                this
                  .#clearLiveRegionText();

              },
              5000
            );
          }


          this.dispatchEvent(
            new CartAddEvent(
              {},
              id.toString(),
              {
                source:
                  'product-form-component',

                itemCount:
                  addedItemCount,

                productId:
                  this.dataset.productId,

                sections:
                  response.sections,
              }
            )
          );
        }
      )

      .catch(
        (error) => {

          console.error(
            'Pizsy cart error:',
            error
          );

        }
      )

      .finally(
        () => {

          cartPerformance
            .measureFromEvent(
              'add:user-action',
              event
            );

        }
      );
  }


  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      text;
  }


  #clearLiveRegionText() {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      '';
  }


  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate =
    (event) => {

      if (
        event.detail.data.newProduct
      ) {

        this.dataset.productId =
          event.detail
            .data
            .newProduct
            .id;

      } else if (
        event.detail
          .data
          .productId !==
        this.dataset.productId
      ) {

        return;
      }


      const {
        variantId,
        addToCartButtonContainer,
      } = this.refs;


      const currentAddToCartButton =
        addToCartButtonContainer
          ?.refs
          .addToCartButton;


      const newAddToCartButton =
        event.detail
          .data
          .html
          .querySelector(
            '[ref="addToCartButton"]'
          );


      if (
        !currentAddToCartButton
      ) {
        return;
      }


      if (
        event.detail.resource ==
          null ||
        event.detail.resource
          .available ==
          false
      ) {

        addToCartButtonContainer
          .disable();


        this.refs
          .acceleratedCheckoutButtonContainer
          ?.setAttribute(
            'hidden',
            'true'
          );

      } else {

        addToCartButtonContainer
          .enable();


        /*
         * Bundle esetén a gyors
         * checkout továbbra is rejtve marad.
         */

        const productDetails =
          this.closest(
            '[data-testid="product-information-details"]'
          );


        const bundleRoot =
          productDetails
            ?.querySelector(
              '[data-pz-bundle-root]'
            );


        if (
          bundleRoot instanceof
          HTMLElement
        ) {

          this.refs
            .acceleratedCheckoutButtonContainer
            ?.setAttribute(
              'hidden',
              'true'
            );

        } else {

          this.refs
            .acceleratedCheckoutButtonContainer
            ?.removeAttribute(
              'hidden'
            );
        }
      }


      if (
        newAddToCartButton
      ) {

        morph(
          currentAddToCartButton,
          newAddToCartButton
        );
      }


      variantId.value =
        event.detail.resource
          ?.id ??
        '';


      if (
        event.detail.resource
      ) {

        const productVariantMedia =
          event.detail
            .resource
            .featured_media
            ?.preview_image
            ?.src;


        if (
          productVariantMedia
        ) {

          addToCartButtonContainer
            ?.setAttribute(
              'data-product-variant-media',
              productVariantMedia +
                '&width=100'
            );
        }
      }
    };


  #onVariantSelected =
    () => {

      this.refs
        .addToCartButtonContainer
        ?.disable();

    };
}


if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}


/**
 * Flying cart animation.
 */
class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;


  connectedCallback() {
    this.#animate();
  }


  #animate() {
    const rect =
      this.getBoundingClientRect();


    const sourceRect =
      this.source
        .getBoundingClientRect();


    const destinationRect =
      this.destination
        .getBoundingClientRect();


    const offset = {
      x:
        rect.width / 2,

      y:
        rect.height / 2,
    };


    const startPoint = {
      x:
        sourceRect.left +
        sourceRect.width / 2 -
        offset.x,

      y:
        sourceRect.top +
        sourceRect.height / 2 -
        offset.y,
    };


    const endPoint = {
      x:
        destinationRect.left +
        destinationRect.width / 2 -
        offset.x,

      y:
        destinationRect.top +
        destinationRect.height / 2 -
        offset.y,
    };


    const controlPoint1 = {
      x:
        startPoint.x,

      y:
        startPoint.y -
        200,
    };


    const controlPoint2 = {
      x:
        endPoint.x -
        300,

      y:
        endPoint.y -
        100,
    };


    /** @type {number | null} */
    let startTime =
      null;


    const duration =
      600;


    this.style.opacity =
      '1';


    const animate =
      (currentTime) => {

        if (!startTime) {
          startTime =
            currentTime;
        }


        const elapsed =
          currentTime -
          startTime;


        const progress =
          Math.min(
            elapsed /
            duration,
            1
          );


        const position =
          bezierPoint(
            progress,
            startPoint,
            controlPoint1,
            controlPoint2,
            endPoint
          );


        this.style
          .setProperty(
            '--x',
            `${position.x}px`
          );


        this.style
          .setProperty(
            '--y',
            `${position.y}px`
          );


        const scale =
          1 -
          progress *
          0.5;


        this.style
          .setProperty(
            '--scale',
            `${scale}`
          );


        if (
          progress < 1
        ) {

          requestAnimationFrame(
            animate
          );

        } else {

          this.style.opacity =
            '0';


          onAnimationEnd(
            this,
            () =>
              this.remove()
          );
        }
      };


    this.style
      .setProperty(
        '--x',
        `${startPoint.x}px`
      );


    this.style
      .setProperty(
        '--y',
        `${startPoint.y}px`
      );


    requestAnimationFrame(
      animate
    );
  }
}


/**
 * @param {number} t
 * @param {{x:number,y:number}} p0
 * @param {{x:number,y:number}} p1
 * @param {{x:number,y:number}} p2
 * @param {{x:number,y:number}} p3
 */
function bezierPoint(
  t,
  p0,
  p1,
  p2,
  p3
) {
  const cX =
    3 *
    (p1.x - p0.x);


  const bX =
    3 *
    (p2.x - p1.x) -
    cX;


  const aX =
    p3.x -
    p0.x -
    cX -
    bX;


  const cY =
    3 *
    (p1.y - p0.y);


  const bY =
    3 *
    (p2.y - p1.y) -
    cY;


  const aY =
    p3.y -
    p0.y -
    cY -
    bY;


  const x =
    aX *
      Math.pow(
        t,
        3
      ) +
    bX *
      Math.pow(
        t,
        2
      ) +
    cX *
      t +
    p0.x;


  const y =
    aY *
      Math.pow(
        t,
        3
      ) +
    bY *
      Math.pow(
        t,
        2
      ) +
    cY *
      t +
    p0.y;


  return {
    x,
    y,
  };
}


if (
  !customElements.get(
    'fly-to-cart'
  )
) {
  customElements.define(
    'fly-to-cart',
    FlyToCart
  );
}